import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { PhotoSlider } from 'react-photo-view';
import { Item } from './styles';

interface ImageViewerContextValue {
  register: (src: string) => number;
  openAt: (index: number) => void;
}

const ImageViewerContext = createContext<ImageViewerContextValue | null>(null);

const useImageViewer = () => {
  const context = useContext(ImageViewerContext);
  if (!context) throw new Error('ImageViewerItem must be used inside ImageViewer');
  return context;
};

export function ImageViewer({ children }: { children: ReactNode }): JSX.Element {
  const [images, setImages] = useState<string[]>([]);
  const [index, setIndex] = useState<number | null>(null);

  const register = (src: string) => {
    setImages(prev => {
      if (prev.includes(src)) return prev;
      return [...prev, src];
    });
    return images.indexOf(src) !== -1 ? images.indexOf(src) : images.length;
  };

  return (
    <ImageViewerContext.Provider value={{ register, openAt: setIndex }}>
      {children}

      {index !== null && images.length > 0 && (
        <PhotoSlider
          images={images.map(src => ({ src, key: src }))}
          visible
          index={index}
          onIndexChange={setIndex}
          onClose={() => setIndex(null)}
        />
      )}
    </ImageViewerContext.Provider>
  );
}

export function ImageViewerItem({ src, children }: { src: string; children: ReactNode }): JSX.Element {
  const { register, openAt } = useImageViewer();
  const indexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!src) return;
    indexRef.current = register(src);
  }, [src, register]);

  const handleClick = () => {
    if (indexRef.current !== null) {
      openAt(indexRef.current);
    }
  };

  return <Item onClick={handleClick}>{children}</Item>;
}
